import React from 'react';
import PropTypes from 'prop-types';
import { Query } from '@apollo/client/react/components';
import { gql } from '@apollo/client';
import * as immutable from 'object-path-immutable';
import {
  get as lodashGet,
  set as lodashSet,
  uniqBy as lodashUniqBy,
} from 'lodash';
import { parseISO, compareAsc } from 'date-fns';

import { appendUniquely } from 'helpers/array-utils';

import {
  GQL_COMMENT_FRAGMENT,
  GQL_VOICENOTE_FRAGMENT,
  getCommentFragmentFromPusherEvent,
  getVoiceNoteFragmentFromPusherEvent,
} from './fragments';

const GQL_GET_ACTIVITIES = gql`
  query GetActivities(
    $nodeId: ID!
    $beforeCursorComments: String
    $beforeCursorVoiceNotes: String
  ) {
    node(id: $nodeId) {
      __typename
      id
      ... on RecordableInterface {
        comments(last: 100, before: $beforeCursorComments)
          @connection(key: "getComments") {
          __typename
          edges {
            node {
              ...CommentFragment
            }
          }
          pageInfo {
            hasPreviousPage
            startCursor
          }
        }
        voiceNotes(last: 100, before: $beforeCursorVoiceNotes)
          @connection(key: "getVoiceNotes") {
          __typename
          edges {
            node {
              ...VoiceNoteFragment
            }
          }
          pageInfo {
            hasPreviousPage
            startCursor
          }
        }
      }
    }
  }

  ${GQL_COMMENT_FRAGMENT}
  ${GQL_VOICENOTE_FRAGMENT}
`;

const DEFAULT_PAGEINFO = { hasPreviousPage: false, startCursor: null };

const commentsFromData = (data) => lodashGet(data, 'node.comments.edges', []);

const voiceNotesFromData = (data) =>
  lodashGet(data, 'node.voiceNotes.edges', []);

const pageInfoCommentsFromData = (data) =>
  lodashGet(data, 'node.comments.pageInfo', DEFAULT_PAGEINFO);

const pageInfoVoiceNotesFromData = (data) =>
  lodashGet(data, 'node.voiceNotes.pageInfo', DEFAULT_PAGEINFO);

const updateCommentsCacheAddEdge = ({ cache, newEdge, nodeId }) => {
  try {
    const cachedData = cache.readQuery({
      query: GQL_GET_ACTIVITIES,
      variables: { nodeId },
    });
    const cachedEdges = commentsFromData(cachedData);

    const updatedEdges = appendUniquely(
      cachedEdges,
      newEdge,
      (edge) => edge.node.id,
    );

    const newData = immutable.set(cachedData, 'node.comments.edges', updatedEdges);
    cache.writeQuery({
      query: GQL_GET_ACTIVITIES,
      variables: { nodeId },
      data: newData,
    });
  } catch (e) {
    // no cache to update
  }
};

const updateVoiceNotesCacheAddEdge = ({ cache, newEdge, nodeId }) => {
  try {
    const cachedData = cache.readQuery({
      query: GQL_GET_ACTIVITIES,
      variables: { nodeId },
    });
    const cachedEdges = voiceNotesFromData(cachedData);

    const updatedEdges = appendUniquely(
      cachedEdges,
      newEdge,
      (edge) => edge.node.id,
    );

    const newData = lodashSet(
      cachedData,
      'node.voiceNotes.edges',
      updatedEdges,
    );
    cache.writeQuery({
      query: GQL_GET_ACTIVITIES,
      variables: { nodeId },
      data: newData,
    });
  } catch (e) {
    // no cache to update
  }
};

const updateCommentsCacheFromPusherEvent = ({ cache, event }) => {
  updateCommentsCacheAddEdge({
    cache,
    newEdge: {
      __typename: 'CommentEdge',
      node: getCommentFragmentFromPusherEvent(event),
    },
    nodeId: event.recordable_node_id,
  });
};

const updateVoiceNotesCacheFromPusherEvent = ({ cache, event }) => {
  updateVoiceNotesCacheAddEdge({
    cache,
    newEdge: {
      __typename: 'VoiceNoteEdge',
      node: getVoiceNoteFragmentFromPusherEvent(event),
    },
    nodeId: event.recordable_node_id,
  });
};

const mergeNewPage = (oldData, newData) => {
  if (!newData) {
    return oldData;
  }

  return {
    ...newData,
    node: {
      ...newData.node,
      comments: {
        ...newData.node.comments,
        edges: lodashUniqBy(
          [...commentsFromData(newData), ...commentsFromData(oldData)],
          (x) => x.node.id,
        ),
      },
      voiceNotes: {
        ...newData.node.voiceNotes,
        edges: lodashUniqBy(
          [...voiceNotesFromData(newData), ...voiceNotesFromData(oldData)],
          (x) => x.node.id,
        ),
      },
    },
  };
};

const createLoadMore = ({
  fetchMore,
  variables,
  pageInfoComments,
  pageInfoVoiceNotes,
}) => () => {
  fetchMore({
    variables: {
      ...variables,
      beforeCursorComments: pageInfoComments.startCursor,
      beforeCursorVoiceNotes: pageInfoVoiceNotes.startCursor,
    },
    updateQuery: (prev, { fetchMoreResult }) =>
      mergeNewPage(prev, fetchMoreResult),
  });
};

const activityComparator = (a, b) => {
  return compareAsc(parseISO(a.createdAt), parseISO(b.createdAt));
};

const ActivitiesDataProvider = ({ children, variables }) => (
  <Query
    query={GQL_GET_ACTIVITIES}
    variables={variables}
    fetchPolicy="cache-and-network"
  >
    {({ loading, error, data, refetch, fetchMore }) => {
      const comments = commentsFromData(data).map((edge) => edge.node);
      const voiceNotes = voiceNotesFromData(data).map((edge) => edge.node);
      const pageInfoComments = pageInfoCommentsFromData(data);
      const pageInfoVoiceNotes = pageInfoVoiceNotesFromData(data);

      const activities = [...comments, ...voiceNotes].sort(activityComparator);

      const loadMore = createLoadMore({
        fetchMore,
        variables,
        pageInfoComments,
        pageInfoVoiceNotes,
      });
      const canLoadMore =
        pageInfoComments.hasPreviousPage || pageInfoVoiceNotes.hasPreviousPage;

      return children({
        fetchingResults: loading,
        fetchError: !!error,
        comments,
        voiceNotes,
        activities,
        refetch,
        loadMore,
        canLoadMore,
      });
    }}
  </Query>
);

ActivitiesDataProvider.propTypes = {
  children: PropTypes.func,
  variables: PropTypes.shape({
    nodeId: PropTypes.string,
  }),
};

export {
  ActivitiesDataProvider,
  updateCommentsCacheAddEdge,
  updateVoiceNotesCacheAddEdge,
  updateCommentsCacheFromPusherEvent,
  updateVoiceNotesCacheFromPusherEvent,
};
